import {FlowProperty, FlowValidationError} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {FlowPropertiesUtils} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/FlowPropertiesUtils';
import {IWidgetComponentProps} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/widgets/FlowPropertiesWidgets';
import {Col} from '@mintdata/mint-kit/impl/properties/components/col/Col';
import {EditableList} from '@mintdata/mint-kit/impl/properties/components/editableList/EditableList';
import {Row} from '@mintdata/mint-kit/impl/properties/components/row/Row';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import bind from 'bind-decorator';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import _get from 'lodash/get';

interface IListWidgetState
{
    newItem:string;
    isNewItemDirty:boolean;
}

@observer
export class ListWidget extends React.Component<IWidgetComponentProps, any>
{
    @observable
    private _state:IListWidgetState;

    private _focusElement:HTMLInputElement;
    private _refHandlers = {
        focus: (el:HTMLInputElement) => this._focusElement = el,
    };

    constructor(props:IWidgetComponentProps)
    {
        super(props);

        const {prop} = props;
        const {listItem} = prop.definition;

        this._state = {
            newItem: listItem.defaultValue,
            isNewItemDirty: false,
        };
    }

    private _getProps()
    {
        const {prop} = this.props;
        const {value} = prop;

        return {
            items: value == null ? [] : value,
            ...this.props,
        };
    }

    @action.bound
    private _onNewItemChange(value:string)
    {
        this._state.newItem = value;
        this._state.isNewItemDirty = true;
    }

    @bind
    private _onItemChange(index:number)
    {
        const {items, onChange} = this._getProps();

        return action((item:string) =>
        {
            const value = [...items];
            value[index] = item;

            onChange(value);
        });
    }

    @action.bound
    private _onItemAdd()
    {
        const {prop, items, onChange} = this._getProps();
        const {definition} = prop;
        const {listItem} = definition;

        const {newItem} = this._state;

        onChange([...items, newItem]);

        this._state = {
            newItem: listItem.defaultValue,
            isNewItemDirty: false,
        };
    }

    @action.bound
    private _onItemRemove(index:number)
    {
        const {items, onChange} = this._getProps();
        const value = [...items];

        value.splice(index, 1);
        onChange(value);
    }

    @bind
    private _onItemMove(from:number, to:number)
    {
        const {items, onChange} = this._getProps();
        const value = ArrayUtils.reorderArray(items, from, to);

        onChange(value);
    }

    render()
    {
        const fvController = controllers.ui.flowValidation;

        const {items, prop, block} = this._getProps();
        const {listItem} = prop.definition;

        const {newItem} = this._state;

        const newItemProp = new FlowProperty(listItem);
        newItemProp.value = newItem;

        const newItemError = fvController.validateFlowProperties([newItemProp], block)[0];
        const isItemValid = !newItemError;

        // todo: implement isDirty logic for nested properties
        return (
            <div>
                <EditableList
                    isValid={isItemValid}
                    times={items.length || 0}
                    focusElement={this._focusElement}
                    itemRenderer={this._itemRenderer}
                    formRenderer={this._formRenderer(newItemProp, newItemError)}
                    onAdd={this._onItemAdd}
                    onRemove={this._onItemRemove}
                    onMove={this._onItemMove}
                />
            </div>
        );
    }

    private _formRenderer = (newItemProp:FlowProperty,
                             newItemError:FlowValidationError) =>
    {
        return () =>
        {
            const {prop, block, frameColumnNames} = this._getProps();
            const {listItem} = prop.definition;

            const {isNewItemDirty} = this._state;

            const ItemWidget = FlowPropertiesUtils.getWidgetComponent(listItem);

            return (
                <Row>
                    <Col size={4}>
                        <ItemWidget
                            prop={newItemProp}
                            block={block}
                            error={newItemError}
                            frameColumnNames={frameColumnNames}
                            inlineLabel={FlowPropertiesUtils.getPropInlineLabel(newItemProp)}
                            isDirty={isNewItemDirty}
                            onChange={this._onNewItemChange}
                            onRef={this._refHandlers.focus}
                        />
                    </Col>
                </Row>
            );
        };
    };

    private _itemRenderer = (index:number) =>
    {
        const fppController = controllers.ui.flowPropertiesPanel;

        const {items, prop, block, frameColumnNames, error} = this._getProps();
        const {listItem} = prop.definition;

        const itemProp = new FlowProperty(listItem);
        itemProp.value = items[index];

        const nestedErrors = _get(error, 'nested', []) as FlowValidationError[];
        const itemError = nestedErrors.find(({flowPropertyName}) => (
            flowPropertyName === fppController.getArrayPropertyName(listItem.name, index)
        ));

        const ItemWidget = FlowPropertiesUtils.getWidgetComponent(listItem);

        return (
            <Row>
                <Col size={4}>
                    <ItemWidget
                        prop={itemProp}
                        block={block}
                        error={itemError}
                        frameColumnNames={frameColumnNames}
                        onChange={this._onItemChange(index)}
                        isDirty={true}
                    />
                </Col>
            </Row>
        );
    };
}
